import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardBody, Badge } from '@repo/ui';
import { Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';

interface CustomFieldStatusCheckProps {
  fieldName: string;
  fieldId: number;
  enabled: boolean;
}

interface MatterStatus {
  matterId: string;
  hasCustomField: boolean;
  fieldValue?: string | boolean;
  status: 'checking' | 'checked' | 'error';
  error?: string;
  timestamp: Date;
}

interface MatterStatusResult {
  matterId: string;
  hasField: boolean;
  fieldValue?: string | boolean;
}

export function CustomFieldStatusCheck({ 
  fieldName, 
  fieldId, 
  enabled 
}: CustomFieldStatusCheckProps) {
  const [matterStatuses, setMatterStatuses] = useState<MatterStatus[]>([]);
  const [currentMatter, setCurrentMatter] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Mutation for checking custom field status on matters
  const checkStatusMutation = useMutation({
    mutationFn: async (matters: string[]) => {
      setIsCheckingStatus(true);
      setMatterStatuses([]);
      setCurrentMatter(null);

      const results: MatterStatusResult[] = [];
      
      for (const matterId of matters) {
        try {
          setCurrentMatter(matterId);
          
          // Add pending status
          setMatterStatuses(prev => [
            ...prev.filter(m => m.matterId !== matterId),
            {
              matterId,
              hasCustomField: false,
              status: 'checking',
              timestamp: new Date(),
            }
          ]);

          // Check matter custom field status
          const response = await api.get<{ 
            hasField: boolean; 
            fieldValue?: string | boolean; 
          }>(`/clio/matters/${matterId}/custom-fields/${fieldId}/status`);

          results.push({
            matterId,
            hasField: response.hasField,
            fieldValue: response.fieldValue,
          });

          // Update status to checked
          setMatterStatuses(prev => [
            ...prev.filter(m => m.matterId !== matterId),
            {
              matterId,
              hasCustomField: response.hasField,
              fieldValue: response.fieldValue,
              status: 'checked',
              timestamp: new Date(),
            }
          ]);

          // Rate limiting - wait 200ms between requests to avoid 429s
          if (matters.indexOf(matterId) < matters.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }

        } catch (error: any) {
          console.error(`Failed to check matter ${matterId}:`, error);
          
          // Handle rate limiting
          if (error.response?.status === 429) {
            showToast.warning(`Rate limited. Waiting before continuing...`);
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            
            // Retry this matter
            try {
              const response = await api.get<{ 
                hasField: boolean; 
                fieldValue?: string | boolean; 
              }>(`/clio/matters/${matterId}/custom-fields/${fieldId}/status`);

              results.push({
                matterId,
                hasField: response.hasField,
                fieldValue: response.fieldValue,
              });

              setMatterStatuses(prev => [
                ...prev.filter(m => m.matterId !== matterId),
                {
                  matterId,
                  hasCustomField: response.hasField,
                  fieldValue: response.fieldValue,
                  status: 'checked',
                  timestamp: new Date(),
                }
              ]);
            } catch (retryError: any) {
              setMatterStatuses(prev => [
                ...prev.filter(m => m.matterId !== matterId),
                {
                  matterId,
                  hasCustomField: false,
                  status: 'error',
                  error: retryError.response?.data?.message || retryError.message || 'Rate limit exceeded',
                  timestamp: new Date(),
                }
              ]);
            }
          } else {
            setMatterStatuses(prev => [
              ...prev.filter(m => m.matterId !== matterId),
              {
                matterId,
                hasCustomField: false,
                status: 'error',
                error: error.response?.data?.message || error.message || 'Unknown error',
                timestamp: new Date(),
              }
            ]);
          }
        }
      }

      setCurrentMatter(null);
      setIsCheckingStatus(false);
      return results;
    },
    onSuccess: (results) => {
      const withField = results.filter(r => r.hasField).length;
      const withoutField = results.filter(r => !r.hasField).length;
      showToast.success(`Status check complete: ${withField} with field, ${withoutField} without field`);
    },
    onError: (error: any) => {
      setIsCheckingStatus(false);
      setCurrentMatter(null);
      const message = error.response?.data?.message || error.message || 'Failed to check status';
      showToast.error(message);
    },
  });

  // Mutation for getting open/pending matters
  const getOpenMattersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get<{ matters: Array<{ id: string; description: string }> }>('/clio/matters/open-pending');
      return response.matters || [];
    },
    onSuccess: (matters) => {
      const matterIds = matters.map(matter => matter.id);
      checkStatusMutation.mutate(matterIds);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to load matters';
      showToast.error(message);
    },
  });

  const handleCheckStatus = () => {
    getOpenMattersMutation.mutate();
  };

  const handleClear = () => {
    setMatterStatuses([]);
    setCurrentMatter(null);
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const getStatusIcon = (status: MatterStatus) => {
    if (status.status === 'checking') return '⏳';
    if (status.status === 'error') return '❌';
    return status.hasCustomField ? '✅' : '⚪';
  };

  const getStatusBadge = (status: MatterStatus) => {
    if (status.status === 'checking') {
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Checking</Badge>;
    }
    if (status.status === 'error') {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">Error</Badge>;
    }
    return status.hasCustomField 
      ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Has Field</Badge>
      : <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">No Field</Badge>;
  };

  const getFieldValueDisplay = (status: MatterStatus) => {
    if (status.status !== 'checked' || !status.hasCustomField) return null;
    
    if (typeof status.fieldValue === 'boolean') {
      return status.fieldValue ? 'Enabled' : 'Disabled';
    }
    
    return status.fieldValue?.toString() || 'No Value';
  };

  const mattersWithField = matterStatuses.filter(m => m.status === 'checked' && m.hasCustomField).length;
  const mattersWithoutField = matterStatuses.filter(m => m.status === 'checked' && !m.hasCustomField).length;
  const mattersWithError = matterStatuses.filter(m => m.status === 'error').length;

  if (!enabled) {
    return (
      <Card className="border-gray-300 dark:border-gray-600 opacity-50">
        <CardHeader>
          <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400">
            Check Custom Field Status
          </h3>
        </CardHeader>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              The custom field "{fieldName}" must be created first before status can be checked.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="border-xtnd-light">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
              Check Custom Field Status
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Check which matters have the "{fieldName}" custom field
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCheckStatus}
              disabled={getOpenMattersMutation.isPending || isCheckingStatus}
              className="border-xtnd-blue text-xtnd-blue hover:bg-xtnd-blue hover:text-white"
            >
              <Icon name="HiSearch" className={`w-4 h-4 mr-2 ${getOpenMattersMutation.isPending || isCheckingStatus ? 'animate-spin' : ''}`} />
              {getOpenMattersMutation.isPending ? 'Loading...' : isCheckingStatus ? 'Checking...' : 'Check Status'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={isCheckingStatus}
              className="text-gray-600 hover:text-gray-800"
            >
              Clear
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {/* Summary */}
        {matterStatuses.length > 0 && (
          <div className="mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="font-medium text-gray-700 dark:text-gray-300">Total Checked</div>
                <div className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white">
                  {matterStatuses.filter(m => m.status === 'checked').length}
                </div>
              </div>
              <div className="text-center">
                <div className="font-medium text-green-600 dark:text-green-400">With Field</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {mattersWithField}
                </div>
              </div>
              <div className="text-center">
                <div className="font-medium text-gray-600 dark:text-gray-400">Without Field</div>
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {mattersWithoutField}
                </div>
              </div>
              <div className="text-center">
                <div className="font-medium text-red-600 dark:text-red-400">Errors</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {mattersWithError}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Processing */}
        {currentMatter && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="HiRefresh" size="sm" className="animate-spin text-blue-600 dark:text-blue-400" />
              <span className="text-blue-600 dark:text-blue-400">
                Checking matter {currentMatter}...
              </span>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {matterStatuses.map((status, index) => (
            <div key={`${status.matterId}-${index}`} className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <span className="text-lg">{getStatusIcon(status)}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                    Matter {status.matterId}
                  </span>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(status)}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTimestamp(status.timestamp)}
                    </span>
                  </div>
                </div>
                
                {status.status === 'error' && status.error && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {status.error}
                  </p>
                )}
                
                {status.status === 'checked' && status.hasCustomField && (
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <Icon name="HiCheckCircle" size="xs" className="text-green-600 dark:text-green-400" />
                    <span>Field Value: {getFieldValueDisplay(status)}</span>
                  </div>
                )}
                
                {status.status === 'checked' && !status.hasCustomField && (
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <Icon name="HiXCircle" size="xs" className="text-gray-500 dark:text-gray-400" />
                    <span>Custom field not set on this matter</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {!isCheckingStatus && !currentMatter && matterStatuses.length === 0 && (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <Icon name="HiClipboardList" size="2xl" className="mx-auto mb-2" />
                <p>No matters checked yet</p>
                <p className="text-sm">Click "Check Status" to analyze all open/pending matters</p>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
